import type { ReactNode, RefObject } from 'react';
import styled, { css } from 'styled-components';
import { readRawValue } from '../../scopeData/utils/readRawValue';
import { useElementWidth } from './utils';
import { APIEntityType, ArdoqId } from '@ardoq/api-types';
import { s8 } from '@ardoq/design-tokens';
import { CUSTOM_SVG_ICON_SELECTOR } from '@ardoq/icons';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { InlineText, text2, text1, text1Italic } from '@ardoq/typography';
import { TextOverflow } from '@ardoq/popovers';

export const IconContainer = styled.div`
  display: flex;
  align-items: center;
  max-height: 18px;
  max-width: 18px;
  font-size: 18px;
  user-select: none;
  margin-right: ${s8};
  vertical-align: middle;

  > ${CUSTOM_SVG_ICON_SELECTOR} {
    height: 18px;
    width: 18px;
  }
`;

export const FlexWrapper = styled.div<{ $isCommaSeparated?: boolean }>`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  ${({ $isCommaSeparated }) => $isCommaSeparated && afterComaPseudoElement};
`;

export const ReferenceTypeName = styled.span<{ $withEllipsis?: boolean }>`
  ${text1Italic};
  margin: 0 ${s8};
  ${({ $withEllipsis }) =>
    $withEllipsis
      ? css`
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
          max-width: 300px;
        `
      : css`
          flex: 1;
          display: inline-flex;
        `}
`;
export const ENTITY_ID_ATTRIBUTE_KEY = 'data-entity-id';

const afterComaPseudoElement = css`
  &::after {
    content: ',';
    display: inline-block;
    width: 10px;
  }
`;

export const responsiveRowHeight = `
  line-height: 20px;

  @media (min-width: 1440px) {
    line-height: 24px;
  }

  @media (min-width: 1512px) {
    line-height: 25px;
  }

  @media (min-width: 1600px) {
    line-height: 27px;
  }

  @media (min-width: 1728px) {
    line-height: 30px;
  }

  @media (min-width: 2560px) {
    line-height: 32px;
  }
`;

export const Name = styled.span<{
  $withEllipsis?: boolean;
  $isCommaSeparated?: boolean;
}>`
  ${text1};
  ${responsiveRowHeight};
  ${({ $withEllipsis }) =>
    $withEllipsis &&
    css`
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      max-width: 300px;
    `}
  ${({ $isCommaSeparated }) => $isCommaSeparated && afterComaPseudoElement};
`;

const NameWithLighterStyling = styled(Name)`
  ${text2};
  height: 24px;
  line-height: 24px;
  overflow: hidden;
`;

type LightNameProps = {
  name: ReactNode;
  nameRef: RefObject<HTMLSpanElement | null> | undefined;
};

const LightName = ({ name, nameRef }: LightNameProps) => {
  if (!nameRef) {
    return <TextOverflow>{name}</TextOverflow>;
  }
  return (
    <InlineText truncate="only-ellipsis" ref={nameRef}>
      {name}
    </InlineText>
  );
};

export const EntityName = ({
  entityType,
  entityId,
  enhancedScopeData,
  parentEntityId,
  isCommaSeparated,
  hasLabelEllipsis,
  popoverAttributes,
  hideDefaultPopover,
  variant,
  nameRef,
}: {
  entityType: APIEntityType;
  entityId: ArdoqId | number;
  enhancedScopeData: EnhancedScopeData;
  parentEntityId?: ArdoqId;
  popoverAttributes?: { [dataAttribute: string]: string | number | undefined };
  isCommaSeparated?: boolean;
  hasLabelEllipsis?: boolean;
  hideDefaultPopover?: boolean;
  variant?: 'default' | 'light';
  nameRef?: RefObject<HTMLSpanElement | null>;
}) => {
  const { ref, width } = useElementWidth();
  const name = readRawValue(
    entityType,
    entityId,
    'name',
    enhancedScopeData,
    parentEntityId
  );
  const NameComponent = variant === 'light' ? NameWithLighterStyling : Name;
  return (
    <NameComponent
      ref={ref}
      {...{ [ENTITY_ID_ATTRIBUTE_KEY]: entityId }}
      {...(!hideDefaultPopover && popoverAttributes ? popoverAttributes : {})}
      // enforce small overflow (of 5px) to trigger CSS ellpisis
      style={hasLabelEllipsis && width ? { width: width - 5 } : {}}
      $withEllipsis={hasLabelEllipsis}
      $isCommaSeparated={isCommaSeparated}
    >
      {variant === 'light' ? <LightName name={name} nameRef={nameRef} /> : name}
    </NameComponent>
  );
};
